import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';

export const CartPage = () => {
  const {
    t,
    cart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal,
    formatPrice,
    getLocalizedText,
    currency,
  } = useApp();
  const navigate = useNavigate();

  const getItemPrice = (item) => {
    const price = item.product?.prices?.[currency] || item.product?.prices?.ron || 0;
    const discount = item.product?.discount_percent || 0;
    return price * (1 - discount / 100);
  };

  if (cart.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 text-center" data-testid="cart-empty">
          <ShoppingBag className="h-24 w-24 mx-auto text-gray-300 mb-6" />
          <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
            {t('emptyCart')}
          </h1>
          <p className="text-muted-foreground mb-8">
            Start shopping to add items to your cart
          </p>
          <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
            <Link to="/shop">{t('continueShopping')}</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const currencySymbol = currency === 'eur' ? '€' : 'RON';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="cart-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t('home')}</Link>
          <span>/</span>
          <span className="text-foreground">{t('cart')}</span>
        </nav>

        <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-8">{t('cart')}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.product_id}
                className="flex gap-4 p-4 bg-white border border-gray-100 rounded-lg"
                data-testid={`cart-item-${item.product_id}`}
              >
                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=200&auto=format&fit=crop'}
                    alt={getLocalizedText(item.product?.name)}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-medium text-foreground hover:text-primary transition-colors truncate">
                      {getLocalizedText(item.product?.name)}
                    </h3>
                  </Link>
                  <p className="text-lg font-semibold text-primary mt-1">
                    {getItemPrice(item).toFixed(2)} {currencySymbol}
                  </p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center border rounded-lg">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-10 text-center text-sm">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => removeFromCart(item.product_id)}
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    {(getItemPrice(item) * item.quantity).toFixed(2)} {currencySymbol}
                  </p>
                </div>
              </div>
            ))}

            <div className="flex justify-between items-center pt-4">
              <Button variant="outline" onClick={clearCart} data-testid="clear-cart">
                Clear Cart
              </Button>
              <Button asChild variant="ghost">
                <Link to="/shop">{t('continueShopping')}</Link>
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-surface p-6 rounded-lg sticky top-24" data-testid="order-summary">
              <h2 className="font-heading text-xl font-semibold mb-6">{t('orderSummary')}</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('subtotal')}</span>
                  <span>{cartTotal.toFixed(2)} {currencySymbol}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('shipping')}</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between text-lg font-semibold">
                  <span>{t('total')}</span>
                  <span className="text-primary">{cartTotal.toFixed(2)} {currencySymbol}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full h-12 bg-primary hover:bg-purple-600 text-white rounded-full"
                data-testid="checkout-btn"
              >
                {t('checkout')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Taxes and shipping calculated at checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;
